import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, ChevronDown, Plus, Edit, Trash2, Clock, CheckCircle2, Copy } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { ExtendedLead, Dimensionamento } from "@/components/ClientDetails/types";
import { supabase } from "@/integrations/supabase/client";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { isProdutoLeadExcluidoDoDimensionamentoFv } from "@/constants/leadsProduto";
import "./Dimensionamento.css";

// Função para formatar telefone
const formatPhone = (phone: string | undefined) => {
  if (!phone) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  // Formata no padrão brasileiro (00) 0000-0000
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  // Para números com 11 dígitos (celular com 9 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Retorna o número original se não conseguir formatar
  return phone;
};

// Opções para Tipo de Fixação e Telha
const tipoFixacaoOptions = ["Telhado", "Solo", "Carport"];
const tipoTelhaOptions = ["Telha Metálica", "Telha Cerâmica", "Telha de Fibrocimento", "Laje", "Telha zipada"];

interface MonthlyData {
  consumo?: string;
  tusd?: string;
  te?: string;
  cosip?: string;
  despesaEnergia?: string;
  despesaFatura?: string;
  consumoPonta?: string;
  consumoForaPonta?: string;
  tusdPonta?: string;
  tePonta?: string;
  tusdForaPonta?: string;
  teForaPonta?: string;
  demandaContratada?: string;
  precoDemanda?: string;
  despesaPonta?: string;
  despesaForaPonta?: string;
  despesaDemanda?: string;
}

interface TotalsData {
  totalConsumo: string;
  totalConsumoPonta: string;
  totalConsumoForaPonta: string;
  avgTariff: string;
  totalCosip: string;
  totalDespesaEnergia: string;
  totalDespesaFatura: string;
  totalDespesaPonta: string;
  totalDespesaForaPonta: string;
  totalDemandaContratada: string;
  totalPrecoDemanda: string;
  totalDespesaDemanda: string;
}

interface Step1InformacoesClienteProps {
  leads: ExtendedLead[];
  selectedLead: ExtendedLead | null;
  onSelectLead: (leadId: string) => void;
  onNext: (dimensionamentoId?: string) => void;
  readOnly?: boolean;
  allowSelectForParceiro?: boolean;
}

export default function Step1InformacoesCliente({ 
  leads, 
  selectedLead, 
  onSelectLead,
  onNext,
  readOnly = false,
  allowSelectForParceiro = false
}: Step1InformacoesClienteProps) {
  const isMobile = useIsMobile();
  const [address, setAddress] = useState("");
  const [number, setNumber] = useState("");
  const [cep, setCep] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [tariffGroup, setTariffGroup] = useState("");
  const [padrao, setPadrao] = useState("");
  const [faturaMinimaKwh, setFaturaMinimaKwh] = useState("");
  const [tipoFixacao, setTipoFixacao] = useState("");
  const [tipoTelha, setTipoTelha] = useState("");
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>(Array(12).fill(null).map(() => ({})));
  const [isDataSaved, setIsDataSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState<Record<string, unknown> | null>(null);
  const [loadedData, setLoadedData] = useState<Record<string, unknown> | null>(null);

  // Estados para gerenciar dimensionamentos
  const [dimensionamentos, setDimensionamentos] = useState<Dimensionamento[]>([]);
  const [selectedDimensionamento, setSelectedDimensionamento] = useState<Dimensionamento | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newDimensionamentoNome, setNewDimensionamentoNome] = useState("");
  const [newDimensionamentoDescricao, setNewDimensionamentoDescricao] = useState("");
  const [isCreatingDimensionamento, setIsCreatingDimensionamento] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dimensionamentoToDelete, setDimensionamentoToDelete] = useState<Dimensionamento | null>(null);
  const [isDeletingDimensionamento, setIsDeletingDimensionamento] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isImportingDadosMensais, setIsImportingDadosMensais] = useState(false);

  // Garantir que body/html não permitam overflow horizontal no mobile
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflowX = 'hidden';
      document.documentElement.style.overflowX = 'hidden';
      document.body.style.maxWidth = '100vw';
      document.documentElement.style.maxWidth = '100vw';
    }

    // Cleanup: restaurar estilos quando o componente for desmontado ou não for mais mobile
    return () => {
      if (isMobile) {
        document.body.style.overflowX = '';
        document.documentElement.style.overflowX = '';
        document.body.style.maxWidth = '';
        document.documentElement.style.maxWidth = '';
      }
    };
  }, [isMobile]);

  // Função para carregar dimensionamentos de um lead
  const loadDimensionamentos = useCallback(async (leadId: string) => {
    try {
      const { data, error } = await supabase
        .from('dimensionamentos')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao carregar dimensionamentos:', error);
        return;
      }

      // Para cada dimensionamento, verificar se está finalizado
      const dimensionamentosComStatus = await Promise.all((data || []).map(async (dim) => {
        try {
          const { data: proposta, error: propostaError } = await supabase
            .from('propostas_clientes')
            .select('dimensionamento_finalizado')
            .eq('lead_id', leadId)
            .eq('dimensionamento_id', dim.id)
            .maybeSingle(); // Usar maybeSingle em vez de single para evitar erro quando não há dados

          if (propostaError) {
            console.log('Erro ao consultar propostas_clientes:', propostaError);
            return {
              ...dim,
              finalizado: false
            };
          }

          return {
            ...dim,
            finalizado: proposta?.dimensionamento_finalizado || false
          };
        } catch (error) {
          // Se não há proposta, o dimensionamento não está finalizado
          console.log('Erro ao verificar status do dimensionamento:', error);
          return {
            ...dim,
            finalizado: false
          };
        }
      }));

      setDimensionamentos(dimensionamentosComStatus);
      
      // Se não há dimensionamentos, limpar seleção
      if (!data || data.length === 0) {
        setSelectedDimensionamento(null);
      }
    } catch (error) {
      console.error('Erro ao carregar dimensionamentos:', error);
    }
  }, []);

  // Validar que o lead selecionado não é de carregador veicular
  useEffect(() => {
    if (selectedLead && isProdutoLeadExcluidoDoDimensionamentoFv(selectedLead.produto)) {
      alert('Leads de instalação de wallbox / carregador veicular não podem passar pelo dimensionamento solar.');
      onSelectLead(''); // Limpar seleção
    }
  }, [selectedLead, onSelectLead]);

  // Função para criar novo dimensionamento
  const createDimensionamento = async () => {
    if (!selectedLead?.id || !newDimensionamentoNome.trim()) {
      return;
    }

    setIsCreatingDimensionamento(true);
    try {
      const { data, error } = await supabase
        .from('dimensionamentos')
        .insert({
          lead_id: selectedLead.id,
          nome_dimensionamento: newDimensionamentoNome.trim(),
          descricao_dimensionamento: newDimensionamentoDescricao.trim() || null,
          grupo_tarifario: tariffGroup
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar dimensionamento:', error);
        alert('Erro ao criar dimensionamento: ' + error.message);
        return;
      }

      // Atualizar lista de dimensionamentos
      await loadDimensionamentos(selectedLead.id);
      
      // Selecionar o novo dimensionamento
      setSelectedDimensionamento(data);
      
      // Carregar dados do dimensionamento selecionado
      await loadSavedData(selectedLead.id, data.id);
      
      // Limpar formulário e fechar dialog
      setNewDimensionamentoNome("");
      setNewDimensionamentoDescricao("");
      setShowCreateDialog(false);
      
    } catch (error) {
      console.error('Erro ao criar dimensionamento:', error);
      alert('Erro ao criar dimensionamento');
    } finally {
      setIsCreatingDimensionamento(false);
    }
  };

  // Função para selecionar dimensionamento
  const selectDimensionamento = (dimensionamento: Dimensionamento) => {
    setSelectedDimensionamento(dimensionamento);
    // Carregar dados do dimensionamento selecionado
    loadSavedData(selectedLead!.id, dimensionamento.id);
  };

  // Função para abrir dialog de confirmação de exclusão
  const confirmDeleteDimensionamento = (dimensionamento: Dimensionamento) => {
    setDimensionamentoToDelete(dimensionamento);
    setShowDeleteDialog(true);
  };

  // Função para deletar dimensionamento
  const deleteDimensionamento = async () => {
    if (!dimensionamentoToDelete) return;

    setIsDeletingDimensionamento(true);
    try {
      // Fazer requisição para deletar dimensionamento
      const response = await fetchWithAuth(`/api/dimensionamentos/${dimensionamentoToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao deletar dimensionamento');
      }

      // Se o dimensionamento deletado era o selecionado, limpar seleção
      if (selectedDimensionamento?.id === dimensionamentoToDelete.id) {
        setSelectedDimensionamento(null);
        // Limpar todos os dados do formulário
        setCep("");
        setAddress("");
        setNumber("");
        setCity("");
        setState("");
        setTariffGroup("");
        setPadrao("");
        setFaturaMinimaKwh("");
        setTipoFixacao("");
        setTipoTelha("");
        setMonthlyData(Array(12).fill(null).map(() => ({})));
        setIsDataSaved(false);
        setHasUnsavedChanges(false);
      }

      // Recarregar lista de dimensionamentos
      await loadDimensionamentos(selectedLead!.id);

      // Fechar dialog e limpar estado
      setShowDeleteDialog(false);
      setDimensionamentoToDelete(null);

      alert('Dimensionamento deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar dimensionamento:', error);
      alert('Erro ao deletar dimensionamento: ' + (error as Error).message);
    } finally {
      setIsDeletingDimensionamento(false);
    }
  };

  // Função para carregar dados salvos anteriormente
  const loadSavedData = useCallback(async (leadId: string, dimensionamentoId?: string) => {
    setIsLoading(true);
    setHasUnsavedChanges(false); // Resetar flag de mudanças durante carregamento

    try {
      // Buscar dados básicos do lead usando fetchWithAuth
      const leadResponse = await fetchWithAuth(`/api/leads/${leadId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (leadResponse.ok) {
        const leadData = await leadResponse.json();

        // Campos básicos
        setCep(leadData.cep || "");
        setAddress(leadData.endereco || "");
        setNumber(leadData.numero || "");
        setCity(leadData.cidade || "");
        setState(leadData.estado || "");

        // Forçar atualização dos campos específicos com delay mínimo
        setTimeout(() => {
          // Forçar atualização dos estados críticos
                  setTariffGroup(leadData.grupo_tarifario || "");
        setPadrao(leadData.padrao || "");
        setFaturaMinimaKwh(leadData.fatura_minima_kwh != null ? String(leadData.fatura_minima_kwh) : "");
        setTipoFixacao(leadData.tipo_fixacao || leadData.tipoFixacao || "");
        setTipoTelha(leadData.tipo_telha || leadData.tipoTelha || "");

          // Tentar forçar re-render dos componentes Select
          setTimeout(() => {
            try {
              // Procurar pelos elementos select e forçar atualização
              const allComboboxes = document.querySelectorAll('[role="combobox"]');
              const padraoSelect = Array.from(allComboboxes).find(el =>
                el.previousElementSibling?.textContent?.includes('Padrão')
              );
              const grupoSelect = Array.from(allComboboxes).find(el =>
                el.previousElementSibling?.textContent?.includes('Grupo')
              );

              if (padraoSelect && leadData.padrao) {
                // Simular mudança no select
                const event = new Event('change', { bubbles: true });
                padraoSelect.dispatchEvent(event);
              }

              if (grupoSelect && leadData.grupo_tarifario) {
                // Simular mudança no select
                const event = new Event('change', { bubbles: true });
                grupoSelect.dispatchEvent(event);
              }
            } catch (error) {
              console.error('Erro ao forçar atualização dos selects:', error);
            }
          }, 100);
        }, 10);
        
        let hasMonthlyData = false;
        let fullData: MonthlyData[] = Array(12).fill(null).map(() => ({}));
        
        if (leadData.grupo_tarifario) {
          let targetTable;
          switch (leadData.grupo_tarifario) {
            case 'Grupo A':
              targetTable = 'clientes_grupoa';
              break;
            case 'Grupo B':
              targetTable = 'clientes_grupob';
              break;
            case 'Mercado Livre':
              targetTable = 'clientes_ml';
              break;
            default:
              break;
          }
          
          if (targetTable) {
            try {
              const monthlyResponse = await fetchWithAuth(`/api/leads/${leadId}/dimensionamento?grupo=${targetTable}&dimensionamento_id=${dimensionamentoId}`, {
                headers: {
                  'Content-Type': 'application/json'
                }
              });
              
              if (monthlyResponse.ok) {
                const monthlyResult = await monthlyResponse.json();
                
                if (monthlyResult.dados_mensais && Array.isArray(monthlyResult.dados_mensais)) {
                  hasMonthlyData = monthlyResult.dados_mensais.length > 0;
                  
                  const convertedData = monthlyResult.dados_mensais.map((item: Record<string, unknown>) => {
                    if (leadData.grupo_tarifario === 'Grupo B') {
                      const consumo = item.consumo || "";
                      const tusd = item.tusd || "";
                      const te = item.te || "";
                      const cosip = item.cosip || "";
                      const despesaEnergia = calculateDespesaEnergia(String(te), String(tusd), String(consumo));
                      const despesaFatura = calculateDespesaFatura(despesaEnergia, String(cosip));
                      return { consumo, tusd, te, cosip, despesaEnergia, despesaFatura };
                    } else {
                      const consumoPonta = item.consumo_ponta || "";
                      const consumoForaPonta = item.consumo_fora_ponta || "";
                      const tusdPonta = item.tusd_ponta || "";
                      const tePonta = item.te_ponta || "";
                      const tusdForaPonta = item.tusd_fora_ponta || "";
                      const teForaPonta = item.te_fora_ponta || "";
                      const demandaContratada = item.demanda_contratada || "";
                      const precoDemanda = item.preco_demanda || "";
                      const cosip = item.cosip || "";
                      const despesaPonta = calculateDespesaEnergia(String(tePonta), String(tusdPonta), String(consumoPonta));
                      const despesaForaPonta = calculateDespesaEnergia(String(teForaPonta), String(tusdForaPonta), String(consumoForaPonta));
                      const despesaDemanda = calculateDespesaDemanda(String(demandaContratada), String(precoDemanda));
                      return { consumoPonta, consumoForaPonta, tusdPonta, tePonta, tusdForaPonta, teForaPonta, demandaContratada, precoDemanda, cosip, despesaPonta, despesaForaPonta, despesaDemanda };
                    }
                  });
                  
                  fullData = Array(12).fill(null).map((_, index) => convertedData[index] || {});
                  setMonthlyData(fullData);
                }
              }
            } catch (monthlyError) {
              console.error('Erro ao carregar dados mensais:', monthlyError);
            }
          }
        }
        
        // Configurar dados originais imediatamente após definir os estados
        const originalDataToStore = {
          cep: leadData.cep || "",
          address: leadData.endereco || "",
          number: leadData.numero || "",
          city: leadData.cidade || "",
          state: leadData.estado || "",
          tariffGroup: leadData.grupo_tarifario || "",
          padrao: leadData.padrao || "",
          faturaMinimaKwh: leadData.fatura_minima_kwh != null ? String(leadData.fatura_minima_kwh) : "",
          tipoFixacao: leadData.tipo_fixacao || leadData.tipoFixacao || "",
          tipoTelha: leadData.tipo_telha || leadData.tipoTelha || "",
          monthlyData: fullData.map(month => ({ ...month })) // Criar uma cópia profunda
        };

        console.log('📋 Configurando dados originais:', originalDataToStore);
        setOriginalData(originalDataToStore);
        
        // Verificar se todos os dados obrigatórios estão preenchidos
        const padraoOk = leadData.padrao && (leadData.padrao !== "Múltiplas Faturas" || (leadData.fatura_minima_kwh != null && Number(leadData.fatura_minima_kwh) > 0));
        if (leadData.grupo_tarifario && padraoOk && (leadData.tipo_fixacao || leadData.tipoFixacao) && (leadData.cep || leadData.endereco) && hasMonthlyData) {
          setIsDataSaved(true);
        } else {
          setIsDataSaved(false);
        }
        
        // Garantir que não há mudanças não salvas após carregar dados
        setHasUnsavedChanges(false);
        
        // Aguardar um ciclo para garantir que os estados foram atualizados
        setTimeout(() => {
          // Verificar se realmente não há mudanças
          const hasChanges = detectChanges();
          if (!hasChanges) {
            setHasUnsavedChanges(false);
          } else {
            // Se ainda há mudanças detectadas, forçar o reset
            setHasUnsavedChanges(false);
          }
        }, 200); // Aumentar o delay para garantir que os estados foram atualizados
      }
    } catch (error) {
      console.error('Erro ao carregar dados salvos:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLead) {
      if (selectedLead.id) {
        // Carregar dimensionamentos do lead
        loadDimensionamentos(selectedLead.id);
        // Não carregar dados automaticamente - aguardar seleção do dimensionamento
      } else {
        setCep("");
        setAddress("");
        setNumber("");
        setCity("");
        setState("");
        setTariffGroup("");
        setPadrao("");
        setFaturaMinimaKwh("");
        setTipoFixacao("");
        setTipoTelha("");
        setMonthlyData(Array(12).fill(null).map(() => ({})));
        setIsDataSaved(false);
        setHasUnsavedChanges(false);
        setOriginalData(null);
        setDimensionamentos([]);
        setSelectedDimensionamento(null);
      }
    }
  }, [selectedLead, loadDimensionamentos]);


  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  const formatBrazilianNumber = (value: string | number): string => {
    if (!value || value === "") return "0,00";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "0,00";
    return numValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCEP = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    const limitedValue = cleanValue.slice(0, 8);
    if (limitedValue.length === 8) {
      return limitedValue.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    }
    return limitedValue;
  };

  const fetchAddressFromCEP = async (cepValue: string) => {
    const cleanCEP = cepValue.replace(/\D/g, '');
    if (cleanCEP.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (!data.erro) {
          setCity(data.localidade || "");
          setState(data.uf || "");
          if (data.logradouro) {
            setAddress(data.logradouro || "");
          } else {
            setAddress("");
          }
        } else {
          setAddress("");
          setCity("");
          setState("");
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        setAddress("");
        setCity("");
        setState("");
      }
    }
  };

  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedCEP = formatCEP(e.target.value);
    setCep(formattedCEP);
    if (formattedCEP.length === 0) {
      setAddress("");
      setCity("");
      setState("");
      return;
    }
    if (formattedCEP.length === 9) {
      setTimeout(() => {
        fetchAddressFromCEP(formattedCEP);
      }, 500);
    } else if (formattedCEP.length < 8) {
      setAddress("");
      setCity("");
      setState("");
    }
  };

  const calculateDespesaEnergia = (te: string, tusd: string, consumo: string) => {
    const valorTe = parseFloat(te) || 0;
    const valorTusd = parseFloat(tusd) || 0;
    const valorConsumo = parseFloat(consumo) || 0;
    return (valorTe * valorConsumo + valorTusd * valorConsumo).toFixed(2);
  };

  const calculateDespesaFatura = (despesaEnergia: string, cosip: string) => {
    const valorDespesaEnergia = parseFloat(despesaEnergia) || 0;
    const valorCosip = parseFloat(cosip) || 0;
    return (valorDespesaEnergia + valorCosip).toFixed(2);
  };

  const calculateDespesaDemanda = (demandaContratada: string, precoDemanda: string) => {
    const valorDemanda = parseFloat(demandaContratada) || 0;
    const valorPreco = parseFloat(precoDemanda) || 0;
    return (valorDemanda * valorPreco).toFixed(2);
  };


  const detectChanges = useCallback(() => {
    if (!originalData) {
      return false;
    }
    // Função auxiliar para normalizar valores para comparação
    const normalizeValue = (value: unknown): string => {
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim();
    };
    
    // Função auxiliar para comparar arrays de dados mensais
    const compareMonthlyData = (current: MonthlyData[], original: MonthlyData[]): boolean => {
      if (!Array.isArray(current) || !Array.isArray(original)) {
        return false;
      }
      if (current.length !== original.length) {
        return true;
      }
      
      for (let i = 0; i < current.length; i++) {
        const currentMonth = current[i] || {};
        const originalMonth = original[i] || {};
        
        // Comparar campos específicos baseado no grupo tarifário
        if (tariffGroup === "Grupo B") {
          const fields = ['consumo', 'tusd', 'te', 'cosip'];
          for (const field of fields) {
            const currentVal = normalizeValue(currentMonth[field]);
            const originalVal = normalizeValue(originalMonth[field]);
            if (currentVal !== originalVal) {
              return true;
            }
          }
        } else if (tariffGroup === "Grupo A" || tariffGroup === "Mercado Livre") {
          const fields = ['consumoPonta', 'consumoForaPonta', 'tusdPonta', 'tePonta', 'tusdForaPonta', 'teForaPonta', 'demandaContratada', 'precoDemanda', 'cosip'];
          for (const field of fields) {
            const currentVal = normalizeValue(currentMonth[field]);
            const originalVal = normalizeValue(originalMonth[field]);
            if (currentVal !== originalVal) {
              return true;
            }
          }
        }
      }
      return false;
    };
    
    // Comparar campos básicos
    const basicFieldsChanged = 
      normalizeValue(cep) !== normalizeValue(originalData.cep) ||
      normalizeValue(address) !== normalizeValue(originalData.address) ||
      normalizeValue(number) !== normalizeValue(originalData.number) ||
      normalizeValue(city) !== normalizeValue(originalData.city) ||
      normalizeValue(state) !== normalizeValue(originalData.state) ||
      normalizeValue(tariffGroup) !== normalizeValue(originalData.tariffGroup) ||
      normalizeValue(padrao) !== normalizeValue(originalData.padrao) ||
      normalizeValue(faturaMinimaKwh) !== normalizeValue((originalData as { faturaMinimaKwh?: string }).faturaMinimaKwh ?? "") ||
      normalizeValue(tipoFixacao) !== normalizeValue(originalData.tipoFixacao) ||
      normalizeValue(tipoTelha) !== normalizeValue(originalData.tipoTelha);
    const monthlyDataChanged = compareMonthlyData(monthlyData, originalData.monthlyData as MonthlyData[]);

    const hasChanges = basicFieldsChanged || monthlyDataChanged;

    setHasUnsavedChanges(hasChanges);

    // Retornar objeto com informações detalhadas sobre o tipo de mudança
    return {
      hasChanges,
      basicFieldsChanged,
      monthlyDataChanged
    };
  }, [address, number, cep, city, state, tariffGroup, padrao, faturaMinimaKwh, tipoFixacao, tipoTelha, monthlyData, originalData, selectedLead]);

  useEffect(() => {
    // Só executar detectChanges se não estiver carregando e tiver dados originais
    if (originalData && !isLoading) {
      const changeInfo = detectChanges();
      // Se não há mudanças e os dados estão salvos, garantir que o estado está correto
      if (!changeInfo.hasChanges && isDataSaved) {
        setHasUnsavedChanges(false);
      }
    }
  }, [detectChanges, originalData, isLoading, isDataSaved]);

  // useEffect separado para resetar flags quando os dados são carregados
  useEffect(() => {
    if (originalData && !isLoading) {
      // Verificar se realmente não há mudanças após o carregamento
      const changeInfo = detectChanges();
      if (!changeInfo.hasChanges) {
        setHasUnsavedChanges(false);
        // Se os dados estão completos, marcar como salvos (número é opcional)
        const padraoOk = padrao && (padrao !== "Múltiplas Faturas" || (faturaMinimaKwh.trim() && !isNaN(parseFloat(faturaMinimaKwh)) && parseFloat(faturaMinimaKwh) > 0));
        if (cep && address && city && state && tariffGroup && padraoOk && tipoFixacao &&
            (tipoFixacao !== 'Telhado' || tipoTelha) && monthlyData.some(data => Object.keys(data).length > 0)) {
          setIsDataSaved(true);
        }
      } else {
        // Se ainda há mudanças detectadas, manter hasUnsavedChanges como true
        setHasUnsavedChanges(true);
      }
    }
  }, [originalData, isLoading, detectChanges, address, cep, city, monthlyData, number, padrao, faturaMinimaKwh, state, tariffGroup, tipoFixacao, tipoTelha]); // Incluir todas as dependências necessárias

  const calculateTotals = useCallback((): TotalsData => {
    const totals: TotalsData = {
      totalConsumo: "0",
      totalConsumoPonta: "0",
      totalConsumoForaPonta: "0",
      avgTariff: "0",
      totalCosip: "0",
      totalDespesaEnergia: "0",
      totalDespesaFatura: "0",
      totalDespesaPonta: "0",
      totalDespesaForaPonta: "0",
      totalDemandaContratada: "0",
      totalPrecoDemanda: "0",
      totalDespesaDemanda: "0"
    };
    
    if (tariffGroup === "Grupo B") {
      const totalConsumo = monthlyData.reduce((sum, data) => sum + (parseFloat(data.consumo || "0") || 0), 0);
      const totalCosip = monthlyData.reduce((sum, data) => sum + (parseFloat(data.cosip || "0") || 0), 0);
      const totalDespesaEnergia = monthlyData.reduce((sum, data) => sum + (parseFloat(data.despesaEnergia || "0") || 0), 0);
      const totalDespesaFatura = monthlyData.reduce((sum, data) => sum + (parseFloat(data.despesaFatura || "0") || 0), 0);
      const tariffs = monthlyData.map(data => (parseFloat(data.te || "0") || 0) + (parseFloat(data.tusd || "0") || 0));
      const avgTariff = tariffs.reduce((sum, tariff) => sum + tariff, 0) / (tariffs.length || 1);
      
      totals.totalConsumo = totalConsumo.toFixed(2);
      totals.avgTariff = avgTariff.toFixed(2);
      totals.totalCosip = totalCosip.toFixed(2);
      totals.totalDespesaEnergia = totalDespesaEnergia.toFixed(2);
      totals.totalDespesaFatura = totalDespesaFatura.toFixed(2);
    } else if (tariffGroup === "Grupo A" || tariffGroup === "Mercado Livre") {
      const totalConsumoPonta = monthlyData.reduce((sum, data) => sum + (parseFloat(data.consumoPonta || "0") || 0), 0);
      const totalConsumoForaPonta = monthlyData.reduce((sum, data) => sum + (parseFloat(data.consumoForaPonta || "0") || 0), 0);
      const totalCosip = monthlyData.reduce((sum, data) => sum + (parseFloat(data.cosip || "0") || 0), 0);
      const totalDespesaPonta = monthlyData.reduce((sum, data) => sum + (parseFloat(data.despesaPonta || "0") || 0), 0);
      const totalDespesaForaPonta = monthlyData.reduce((sum, data) => sum + (parseFloat(data.despesaForaPonta || "0") || 0), 0);
      const totalDemandaContratada = monthlyData.reduce((sum, data) => sum + (parseFloat(data.demandaContratada || "0") || 0), 0);
      const totalPrecoDemanda = monthlyData.reduce((sum, data) => sum + (parseFloat(data.precoDemanda || "0") || 0), 0);
      const totalDespesaDemanda = monthlyData.reduce((sum, data) => sum + (parseFloat(data.despesaDemanda || "0") || 0), 0);
      
      // Calcular despesa total da fatura (soma de todos os valores)
      const totalDespesaFatura = totalDespesaPonta + totalDespesaForaPonta + totalCosip + totalDespesaDemanda;
      
      totals.totalConsumoPonta = totalConsumoPonta.toFixed(2);
      totals.totalConsumoForaPonta = totalConsumoForaPonta.toFixed(2);
      totals.totalCosip = totalCosip.toFixed(2);
      totals.totalDespesaPonta = totalDespesaPonta.toFixed(2);
      totals.totalDespesaForaPonta = totalDespesaForaPonta.toFixed(2);
      totals.totalDemandaContratada = totalDemandaContratada.toFixed(2);
      totals.totalPrecoDemanda = totalPrecoDemanda.toFixed(2);
      totals.totalDespesaDemanda = totalDespesaDemanda.toFixed(2);
      totals.totalDespesaFatura = totalDespesaFatura.toFixed(2);
    }
    
    return totals;
  }, [monthlyData, tariffGroup]);

  const [totals, setTotals] = useState<TotalsData>(calculateTotals());

  useEffect(() => {
    setTotals(calculateTotals());
  }, [monthlyData, tariffGroup, calculateTotals]);


  const validateData = () => {
    // Validar campos de endereço obrigatórios
    if (!cep) {
      alert("Por favor, preencha o CEP.");
      return false;
    }
    if (!address) {
      alert("Por favor, preencha o endereço.");
      return false;
    }
    // Número é opcional, não precisa validar
    if (!city) {
      alert("Por favor, preencha a cidade.");
      return false;
    }
    if (!state) {
      alert("Por favor, preencha o estado.");
      return false;
    }
    if (!tariffGroup) {
      alert("Por favor, selecione o grupo tarifário.");
      return false;
    }
    if (!padrao) {
      alert("Por favor, selecione o padrão (monofásico, bifásico, trifásico ou múltiplas faturas).");
      return false;
    }
    if (padrao === "Múltiplas Faturas") {
      const v = parseFloat(faturaMinimaKwh);
      if (!faturaMinimaKwh.trim() || isNaN(v) || v <= 0) {
        alert("Para múltiplas faturas, informe a Fatura Mínima (kWh) – soma das faturas mínimas do conjunto (ex.: 130).");
        return false;
      }
    }
    if (!tipoFixacao) {
      alert("Por favor, selecione o tipo de fixação.");
      return false;
    }
    if (tipoFixacao === 'Telhado' && !tipoTelha) {
      alert("Por favor, selecione o tipo de telha.");
      return false;
    }
    
    // Validar que todos os meses têm dados completos
    let allMonthsValid = true;
    const invalidMonths: number[] = [];
    
    if (tariffGroup === "Grupo B") {
      monthlyData.forEach((data, index) => {
        if (!(data.consumo && data.tusd && data.te && data.cosip)) {
          allMonthsValid = false;
          invalidMonths.push(index);
        }
      });
    } else if (tariffGroup === "Grupo A" || tariffGroup === "Mercado Livre") {
      monthlyData.forEach((data, index) => {
        if (!(data.consumoPonta && data.consumoForaPonta && data.tusdPonta && data.tePonta && data.tusdForaPonta && data.teForaPonta && data.demandaContratada && data.precoDemanda && data.cosip)) {
          allMonthsValid = false;
          invalidMonths.push(index);
        }
      });
    }
    
    if (!allMonthsValid) {
      const monthsNames = invalidMonths.map(index => months[index]).join(", ");
      alert(`Por favor, preencha todos os dados obrigatórios para os seguintes meses: ${monthsNames}`);
      return false;
    }
    
    return true;
  };

  // Função para verificar se há dados preenchidos
  const hasFilledData = () => {
    // Verificar se campos básicos estão preenchidos (número é opcional)
    const basicFieldsFilled = cep && address && city && state && tariffGroup && padrao && tipoFixacao;
    if (!basicFieldsFilled) {
      return false;
    }
    if (padrao === "Múltiplas Faturas") {
      const v = parseFloat(faturaMinimaKwh);
      if (!faturaMinimaKwh.trim() || isNaN(v) || v <= 0) return false;
    }

    // Verificar se tipo de telha está preenchido quando necessário
    if (tipoFixacao === 'Telhado' && !tipoTelha) {
      return false;
    }

    // Verificar se há dados mensais preenchidos
    // Verificar se há pelo menos um mês com pelo menos um campo preenchido
    const hasMonthlyData = monthlyData.some(data => {
      if (!data || Object.keys(data).length === 0) return false;
      
      // Verificar se há pelo menos um campo preenchido baseado no grupo tarifário
      if (tariffGroup === "Grupo B") {
        return !!(data.consumo || data.tusd || data.te || data.cosip);
      } else if (tariffGroup === "Grupo A" || tariffGroup === "Mercado Livre") {
        return !!(data.consumoPonta || data.consumoForaPonta || data.tusdPonta || data.tePonta || 
                  data.tusdForaPonta || data.teForaPonta || data.demandaContratada || data.precoDemanda || data.cosip);
      }
      return false;
    });
    
    return hasMonthlyData;
  };

  // Função para verificar se pode prosseguir para a próxima etapa
  const canProceedToNext = () => {
    // Não pode prosseguir se não há dados preenchidos
    if (!hasFilledData()) {
      return false;
    }
    
    // Não pode prosseguir se os dados não foram salvos
    if (!isDataSaved) {
      return false;
    }
    
    // Não pode prosseguir se há alterações não salvas
    if (hasUnsavedChanges) {
      return false;
    }

    return true;
  };

  // Função para limpar dados das etapas seguintes (2, 3 e 4)
  const clearEtapasSeguintes = async (leadId: string, dimensionamentoId?: string, clearAll: boolean = false) => {
    try {
      const target = clearAll ? 'todos os dimensionamentos' : (dimensionamentoId ? `dimensionamento ${dimensionamentoId}` : 'dimensionamento atual');
      
      // Importar Supabase
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Determinar tabela baseada no grupo tarifário
      let targetTable = 'clientes_grupob';
      if (tariffGroup === 'Grupo A') {
        targetTable = 'clientes_grupoa';
      } else if (tariffGroup === 'Mercado Livre') {
        targetTable = 'clientes_ml';
      }
      
      // Limpar dados da etapa 2 (dimensionamento) - MAS PRESERVAR dados mensais
      let dimensionamentoQuery = supabase
        .from(targetTable)
        .update({
          dimensio: null,
          percentual: null,
          eficiencia: null,
          geracao_anual_kwp: null,
          geracao_mensal: null
        })
        .eq('lead_id', leadId);
      
      // Se não for clearAll e tiver dimensionamento específico, filtrar
      if (!clearAll && dimensionamentoId) {
        dimensionamentoQuery = dimensionamentoQuery.eq('dimensionamento_id', dimensionamentoId);
      }
      
      const { error: dimensionamentoError } = await dimensionamentoQuery;
      
      if (dimensionamentoError) {
        console.warn('Erro ao limpar dados de dimensionamento:', dimensionamentoError);
      }
      
      // Limpar dados da etapa 3 (kits)
      let kitsQuery = supabase
        .from('propostas_clientes')
        .update({
          kit1_codigo: null,
          kit1_nome: null,
          kit1_potencia: null,
          kit1_preco: null,
          kit1_quantidade: null,
          kit1_insumos: null,
          kit2_codigo: null,
          kit2_nome: null,
          kit2_potencia: null,
          kit2_preco: null,
          kit2_quantidade: null,
          kit2_insumos: null,
          kit3_codigo: null,
          kit3_nome: null,
          kit3_potencia: null,
          kit3_preco: null,
          kit3_quantidade: null,
          kit3_insumos: null,
          kit4_codigo: null,
          kit4_nome: null,
          kit4_potencia: null,
          kit4_preco: null,
          kit4_quantidade: null,
          kit4_insumos: null,
          total_kits: null,
          valor_total_kits: null,
          kits_r_wp: null,
          desconto_kits: null,
          kit1_potencia_painel: null,
          kit2_potencia_painel: null,
          kit3_potencia_painel: null,
          kit4_potencia_painel: null,
          kit1_quantidade_paineis: null,
          kit2_quantidade_paineis: null,
          kit3_quantidade_paineis: null,
          kit4_quantidade_paineis: null,
          kit1_modulo_descricao: null,
          kit2_modulo_descricao: null,
          kit3_modulo_descricao: null,
          kit4_modulo_descricao: null,
          dimensionamento_finalizado: false
        })
        .eq('lead_id', leadId);
      
      // Se não for clearAll e tiver dimensionamento específico, filtrar
      if (!clearAll && dimensionamentoId) {
        kitsQuery = kitsQuery.eq('dimensionamento_id', dimensionamentoId);
      }
      
      const { error: kitsError } = await kitsQuery;
      
      if (kitsError) {
        console.warn('Erro ao limpar dados de kits:', kitsError);
      }
      
      // Limpar dados da etapa 4 (projecao_grupob)
      let projecaoQuery = supabase
        .from('projecao_grupob')
        .delete()
        .eq('lead_id', leadId);
      
      // Se não for clearAll e tiver dimensionamento específico, filtrar
      if (!clearAll && dimensionamentoId) {
        projecaoQuery = projecaoQuery.eq('dimensionamento_id', dimensionamentoId);
      }
      
      const { error: projecaoError } = await projecaoQuery;
      
      if (projecaoError) {
        console.warn('Erro ao limpar projecao_grupob:', projecaoError);
      }
      
      // Limpar dados de precificação da tabela propostas_clientes
      let precificacaoQuery = supabase
        .from('propostas_clientes')
        .update({
          valor_estrutura_metallica: null,
          valor_fundacao: null,
          valor_equipamentos_alugados: null,
          custo_materiais: null,
          comissao_vendedor: null,
          comissao_parceiro: null,
          comissao_vendedor_valor: null,
          comissao_parceiro_valor: null,
          integracao_r_wp: null,
          impostos_percentual: null,
          mao_obra_por_placa: null,
          preco_projeto: null,
          preco_integracao: null,
          preco_kits: null,
          preco_estrutura_metallica_calc: null,
          impostos: null,
          mao_obra: null,
          custo_total_implantacao: null,
          custo_total_integracao: null,
          preco_por_wp_final: null,
          lucro_liquido: null,
          lucro_liquido_percentual: null,
          com_sfv: null,
          proposta_pdf_url: null
        })
        .eq('lead_id', leadId);
      
      // Se não for clearAll e tiver dimensionamento específico, filtrar
      if (!clearAll && dimensionamentoId) {
        precificacaoQuery = precificacaoQuery.eq('dimensionamento_id', dimensionamentoId);
      }
      
      const { error: precificacaoError } = await precificacaoQuery;
      
      if (precificacaoError) {
        console.warn('Erro ao limpar dados de precificação e propostas:', precificacaoError);
      }
      
    } catch (error) {
      console.error('❌ Erro ao limpar dados das etapas seguintes:', error);
    }
  };

  const handleSaveData = async () => {
    if (!validateData()) return;
    if (!selectedLead?.id) {
      alert("Nenhum cliente selecionado.");
      return;
    }
    if (!selectedDimensionamento?.id) {
      alert("Nenhum dimensionamento selecionado.");
      return;
    }

    const dataToSend = {
      cep,
      endereco: address,
      numero: number,
      cidade: city,
      estado: state,
      grupo_tarifario: tariffGroup,
      padrao,
      fatura_minima_kwh: padrao === "Múltiplas Faturas" ? (faturaMinimaKwh.trim() ? parseFloat(faturaMinimaKwh) : null) : null,
      tipo_fixacao: tipoFixacao,
      tipo_telha: tipoTelha,
      monthlyData,
      dimensionamento_id: selectedDimensionamento.id
    };

    try {
      const response = await fetchWithAuth(`/api/leads/${selectedLead.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Detectar que tipo de mudanças foram feitas
        const changeInfo = detectChanges();
        
        if (changeInfo.basicFieldsChanged && changeInfo.monthlyDataChanged) {
          // Mudanças em dados básicos E dados mensais - afetar todos os dimensionamentos
          try {
            const { PropostasService } = await import('@/services/propostasService');
            await PropostasService.marcarDimensionamentoNaoFinalizado(selectedLead.id);
          } catch (error) {
            console.error('Erro ao marcar dimensionamentos como não finalizados:', error);
          }
          
          // Limpar dados de TODOS os dimensionamentos
          await clearEtapasSeguintes(selectedLead.id, undefined, true);
          
        } else if (changeInfo.basicFieldsChanged) {
          // Apenas mudanças em dados básicos - afetar todos os dimensionamentos
          try {
            const { PropostasService } = await import('@/services/propostasService');
            await PropostasService.marcarDimensionamentoNaoFinalizado(selectedLead.id);
          } catch (error) {
            console.error('Erro ao marcar dimensionamentos como não finalizados:', error);
          }
          
          // Limpar dados de TODOS os dimensionamentos
          await clearEtapasSeguintes(selectedLead.id, undefined, true);
          
        } else if (changeInfo.monthlyDataChanged) {
          // Apenas mudanças em dados mensais - afetar apenas o dimensionamento específico
          try {
            const { PropostasService } = await import('@/services/propostasService');
            await PropostasService.marcarDimensionamentoNaoFinalizado(selectedLead.id, selectedDimensionamento?.id);
          } catch (error) {
            console.error('Erro ao marcar dimensionamento como não finalizado:', error);
          }
          
          // Limpar dados apenas do dimensionamento específico
          await clearEtapasSeguintes(selectedLead.id, selectedDimensionamento?.id, false);
        }
        
        // Atualizar dados originais com os valores atuais
        const updatedOriginalData = {
          cep,
          address,
          number,
          city,
          state,
          tariffGroup,
          padrao,
          faturaMinimaKwh,
          tipoFixacao,
          tipoTelha,
          monthlyData: monthlyData.map(month => ({ ...month })) // Criar uma cópia profunda do array
        };
        
        setOriginalData(updatedOriginalData);
        setIsDataSaved(true);
        setHasUnsavedChanges(false);
        
        alert("Dados salvos com sucesso!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erro HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Erro detalhado:', error);
      alert(`Erro ao salvar dados: ${error.message}`);
    }
  };



  const replicateValue = (field: keyof MonthlyData, value: string) => {
    if (!value || value === "") return;

    const newData = [...monthlyData];

    newData.forEach((data, index) => {
      if (index > 0) {
        data[field] = value;
        if (tariffGroup === "Grupo B") {
          if (field === 'consumo' || field === 'tusd' || field === 'te') {
            data.despesaEnergia = calculateDespesaEnergia(data.te || "0", data.tusd || "0", data.consumo || "0");
            data.despesaFatura = calculateDespesaFatura(data.despesaEnergia, data.cosip || "0");
          } else if (field === 'cosip') {
            data.despesaFatura = calculateDespesaFatura(data.despesaEnergia || "0", value);
          }
        } else if (tariffGroup === "Grupo A" || tariffGroup === "Mercado Livre") {
          if (field === 'consumoPonta' || field === 'tusdPonta' || field === 'tePonta') {
            data.despesaPonta = calculateDespesaEnergia(data.tePonta || "0", data.tusdPonta || "0", data.consumoPonta || "0");
          } else if (field === 'consumoForaPonta' || field === 'tusdForaPonta' || field === 'teForaPonta') {
            data.despesaForaPonta = calculateDespesaEnergia(data.teForaPonta || "0", data.tusdForaPonta || "0", data.consumoForaPonta || "0");
          } else if (field === 'demandaContratada' || field === 'precoDemanda') {
            data.despesaDemanda = calculateDespesaDemanda(data.demandaContratada || "0", data.precoDemanda || "0");
          }
        }
      }
    });

    setMonthlyData(newData);
  };

  // Importar dados mensais de outro dimensionamento do mesmo cliente
  const handleImportDadosMensais = async (sourceDimensionamento: Dimensionamento) => {
    if (!selectedLead?.id || !tariffGroup) return;
    let targetTable: string;
    switch (tariffGroup) {
      case 'Grupo A':
        targetTable = 'clientes_grupoa';
        break;
      case 'Grupo B':
        targetTable = 'clientes_grupob';
        break;
      case 'Mercado Livre':
        targetTable = 'clientes_ml';
        break;
      default:
        return;
    }
    setIsImportingDadosMensais(true);
    try {
      const response = await fetchWithAuth(
        `/api/leads/${selectedLead.id}/dimensionamento?grupo=${targetTable}&dimensionamento_id=${sourceDimensionamento.id}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error('Erro ao buscar dados');
      const result = await response.json();
      if (!result.dados_mensais || !Array.isArray(result.dados_mensais) || result.dados_mensais.length === 0) {
        alert(
          'O dimensionamento selecionado não possui dados na tabela Dados Mensais. ' +
          'Verifique se o dimensionamento de origem tem o mesmo grupo tarifário (atual: ' + tariffGroup + ').'
        );
        return;
      }
      const convertedData = result.dados_mensais.map((item: Record<string, unknown>) => {
        if (tariffGroup === "Grupo B") {
          const consumo = item.consumo || "";
          const tusd = item.tusd || "";
          const te = item.te || "";
          const cosip = item.cosip || "";
          const despesaEnergia = calculateDespesaEnergia(String(te), String(tusd), String(consumo));
          const despesaFatura = calculateDespesaFatura(despesaEnergia, String(cosip));
          return { consumo, tusd, te, cosip, despesaEnergia, despesaFatura };
        } else {
          const consumoPonta = item.consumo_ponta || "";
          const consumoForaPonta = item.consumo_fora_ponta || "";
          const tusdPonta = item.tusd_ponta || "";
          const tePonta = item.te_ponta || "";
          const tusdForaPonta = item.tusd_fora_ponta || "";
          const teForaPonta = item.te_fora_ponta || "";
          const demandaContratada = item.demanda_contratada || "";
          const precoDemanda = item.preco_demanda || "";
          const cosip = item.cosip || "";
          const despesaPonta = calculateDespesaEnergia(String(tePonta), String(tusdPonta), String(consumoPonta));
          const despesaForaPonta = calculateDespesaEnergia(String(teForaPonta), String(tusdForaPonta), String(consumoForaPonta));
          const despesaDemanda = calculateDespesaDemanda(String(demandaContratada), String(precoDemanda));
          return { consumoPonta, consumoForaPonta, tusdPonta, tePonta, tusdForaPonta, teForaPonta, demandaContratada, precoDemanda, cosip, despesaPonta, despesaForaPonta, despesaDemanda };
        }
      });
      const fullData = Array(12).fill(null).map((_, index) => convertedData[index] || {});
      setMonthlyData(fullData);
      setHasUnsavedChanges(true);
      setIsDataSaved(false);
      setShowImportDialog(false);
    } catch (error) {
      console.error('Erro ao importar dados mensais:', error);
      alert(`Erro ao importar dados: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsImportingDadosMensais(false);
    }
  };

  // Dimensionamentos disponíveis para importar (mesmo cliente, excluindo o atual; preferir mesmo grupo tarifário)
  const dimensionamentosParaImportar = dimensionamentos.filter(
    (d) => d.id !== selectedDimensionamento?.id
  );

  return (
    <div 
      data-step1-container
      className={`space-y-6 ${isMobile ? 'p-4' : 'p-6'} overflow-x-hidden max-w-full w-full`}
      style={{ 
        maxWidth: isMobile ? 'calc(100vw - 2rem)' : '100%', 
        width: '100%', 
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}
    >
      <Card className="w-full overflow-x-hidden min-w-0">
        <CardHeader>
          <CardTitle>Seleção de Cliente</CardTitle>
        </CardHeader>
        <CardContent>
          <Select 
            onValueChange={(leadId) => {
              // Validar que o lead selecionado não é de carregador veicular
              const lead = leads.find(l => l.id === leadId);
              if (lead && isProdutoLeadExcluidoDoDimensionamentoFv(lead.produto)) {
                alert('Leads de instalação de wallbox / carregador veicular não podem passar pelo dimensionamento solar.');
                return;
              }
              onSelectLead(leadId);
            }} 
            value={selectedLead?.id || ""} 
            disabled={readOnly && !allowSelectForParceiro}
          >
            <SelectTrigger className="w-full md:w-1/3 min-w-0">
              <SelectValue placeholder="Selecione um cliente para iniciar o dimensionamento" />
            </SelectTrigger>
            <SelectContent>
              {leads
                .filter((lead) => !isProdutoLeadExcluidoDoDimensionamentoFv(lead.produto))
                .map((lead) => (
                  <SelectItem key={lead.id || lead.nome || lead.razaoSocial} value={lead.id || ""}>
                    {lead.nome || lead.razaoSocial}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedLead && (
        <>
          <Card className="w-full overflow-x-hidden min-w-0">
            <CardHeader>
              <CardTitle>Dados do Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 w-full min-w-0 max-w-full">
              <p className="break-words"><strong>Nome/Razão Social:</strong> {selectedLead.nome || selectedLead.razaoSocial}</p>
              <p className="break-words"><strong>Email:</strong> {selectedLead.email}</p>
              <p className="break-words"><strong>Telefone:</strong> {formatPhone(selectedLead.telefone)}</p>
              <p className="break-words"><strong>Tipo:</strong> {selectedLead.tipo === 'fisica' ? 'Pessoa Física' : 'Pessoa Jurídica'}</p>
            </CardContent>
          </Card>

          {isLoading && (
            <Card className="w-full overflow-x-hidden min-w-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-muted-foreground">Carregando dados salvos...</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="w-full overflow-x-hidden min-w-0 max-w-full" style={{ maxWidth: isMobile ? '100vw' : '100%', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            <CardHeader>
              <CardTitle>Informações do Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 w-full min-w-0 max-w-full overflow-x-hidden" style={{ maxWidth: isMobile ? '100vw' : '100%', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
              {/* Seção de Dimensionamentos */}
              {selectedLead && (
                <div className="border rounded-lg p-4 bg-gray-50 w-full min-w-0 max-w-full">
                  <div className={`flex ${isMobile ? 'flex-col gap-4' : 'items-center justify-between'} mb-4 w-full min-w-0`}>
                    <div className="w-full min-w-0">
                      <h3 className="text-lg font-semibold">Dimensionamentos</h3>
                      <p className="text-sm text-gray-600 mt-1 break-words">
                        Selecione um dimensionamento existente ou crie um novo para continuar.
                      </p>
                    </div>
                    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                      <DialogTrigger asChild>
                        <Button variant="accent" size="sm" disabled={readOnly} className={isMobile ? 'w-full' : ''}>
                          <Plus className="h-4 w-4 mr-2" />
                          Novo Dimensionamento
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Criar Novo Dimensionamento</DialogTitle>
                          <DialogDescription>
                            Crie um novo dimensionamento para {selectedLead.nome || selectedLead.razaoSocial}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="nome-dimensionamento">Nome do Dimensionamento *</Label>
                            <Input
                              id="nome-dimensionamento"
                              value={newDimensionamentoNome}
                              onChange={(e) => setNewDimensionamentoNome(e.target.value)}
                              placeholder="Ex: Casa Principal, Galpão Industrial"
                              disabled={readOnly}
                            />
                          </div>
                          <div>
                            <Label htmlFor="descricao-dimensionamento">Descrição (opcional)</Label>
                            <Textarea
                              id="descricao-dimensionamento"
                              value={newDimensionamentoDescricao}
                              onChange={(e) => setNewDimensionamentoDescricao(e.target.value)}
                              placeholder="Descreva o dimensionamento..."
                              rows={3}
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                            Cancelar
                          </Button>
                          <Button 
                            onClick={createDimensionamento}
                            disabled={!newDimensionamentoNome.trim() || isCreatingDimensionamento || readOnly}
                          >
                            {isCreatingDimensionamento ? "Criando..." : "Criar"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* Dialog de confirmação de exclusão */}
                    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirmar Exclusão</DialogTitle>
                          <DialogDescription>
                            Tem certeza que deseja deletar o dimensionamento "{dimensionamentoToDelete?.nome_dimensionamento}"?
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 space-y-2">
                          <strong>Esta ação irá deletar:</strong>
                          <ul className="list-disc list-inside mt-2 space-y-1">
                            <li>Todos os dados da etapa 1 (informações do cliente)</li>
                            <li>Todos os dados da etapa 2 (dimensionamento do sistema)</li>
                            <li>Todos os dados da etapa 3 (escolha do kit)</li>
                            <li>Todos os dados da etapa 4 (precificação)</li>
                            <li>Todas as propostas relacionadas a este dimensionamento</li>
                          </ul>
                          <p className="mt-4">
                            <strong className="text-red-600">Esta ação não pode ser desfeita!</strong>
                          </p>
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            onClick={() => setShowDeleteDialog(false)}
                            disabled={isDeletingDimensionamento}
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={deleteDimensionamento}
                            disabled={isDeletingDimensionamento}
                          >
                            {isDeletingDimensionamento ? "Deletando..." : "Deletar"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {dimensionamentos.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Nenhum dimensionamento encontrado para este cliente.</p>
                      <p className="text-sm mt-1">Clique em "Novo Dimensionamento" para criar o primeiro.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {dimensionamentos.map((dimensionamento) => (
                        <div
                          key={dimensionamento.id}
                          className={`p-3 border rounded-lg transition-colors w-full min-w-0 max-w-full ${
                            readOnly ? 'cursor-default' : 'cursor-pointer'
                          } ${
                            selectedDimensionamento?.id === dimensionamento.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => !readOnly && selectDimensionamento(dimensionamento)}
                        >
                          <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'} w-full min-w-0`}>
                            <div className="w-full min-w-0 flex-1">
                              <div className={`flex ${isMobile ? 'flex-col' : 'items-center'} gap-2 flex-wrap`}>
                                <h4 className="font-medium break-words">{dimensionamento.nome_dimensionamento}</h4>
                                {/* Indicador de status de finalização */}
                                {dimensionamento.finalizado ? (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex-shrink-0">
                                    <CheckCircle2 className="h-3 w-3" />
                                    <span>Finalizado</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex-shrink-0">
                                    <Clock className="h-3 w-3" />
                                    <span>Em processo</span>
                                  </div>
                                )}
                              </div>
                              {dimensionamento.descricao_dimensionamento && (
                                <p className="text-sm text-gray-600 mt-1 break-words">
                                  {dimensionamento.descricao_dimensionamento}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1 break-words">
                                Criado em: {new Date(dimensionamento.created_at).toLocaleDateString('pt-BR')}
                                {dimensionamento.finalizado && (
                                  <span className="text-green-600 ml-2">• ✓ Pronto para gerar proposta</span>
                                )}
                              </p>
                            </div>
                            <div className={`flex items-center ${isMobile ? 'justify-start gap-2 mt-2' : 'space-x-2 flex-shrink-0'}`}>
                              <span className="text-xs bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                                {dimensionamento.grupo_tarifario}
                              </span>
                              {!readOnly && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmDeleteDimensionamento(dimensionamento);
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                  title="Deletar dimensionamento"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                              {selectedDimensionamento?.id === dimensionamento.id && (
                                <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedDimensionamento && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg w-full min-w-0 max-w-full">
                      <div className="flex items-center w-full min-w-0">
                        <CheckCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                        <span className="text-blue-800 font-medium break-words">
                          Dimensionamento selecionado: {selectedDimensionamento.nome_dimensionamento}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {selectedDimensionamento && (
                <div className="w-full min-w-0 max-w-full overflow-x-hidden mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full min-w-0">
                    <div className="w-full min-w-0">
                      <Label htmlFor="cep">CEP</Label>
                      <Input id="cep" value={cep} onChange={handleCEPChange} placeholder="00000-000" maxLength={9} disabled={readOnly} className="w-full" />
                    </div>
                    <div className="w-full min-w-0">
                      <Label htmlFor="address">Endereço</Label>
                      <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua, Avenida, etc." disabled={readOnly} className="w-full" />
                    </div>
                    <div className="w-full min-w-0">
                      <Label htmlFor="number">Número (opcional)</Label>
                      <Input id="number" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="123" disabled={readOnly} className="w-full" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mt-4 min-w-0">
                    <div className="w-full min-w-0">
                      <Label htmlFor="city">Cidade</Label>
                      <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Nome da cidade" disabled={readOnly} className="w-full" />
                    </div>
                    <div className="w-full min-w-0">
                      <Label htmlFor="state">Estado</Label>
                      <Input id="state" value={state} onChange={(e) => setState(e.target.value)} placeholder="UF" maxLength={2} disabled={readOnly} className="w-full" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mt-4 min-w-0">
                    <div className="w-full min-w-0">
                      <Label>Tipo de Fixação *</Label>
                      <Select value={tipoFixacao} onValueChange={(value) => {
                        if (tipoFixacao === 'Telhado' && value !== 'Telhado') {
                          setTipoTelha("");
                        }
                        setTipoFixacao(value);
                      }} disabled={readOnly}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecione o tipo de fixação" />
                        </SelectTrigger>
                        <SelectContent>
                          {tipoFixacaoOptions.map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="w-full min-w-0">
                      <Label>Grupo Tarifário *</Label>
                      <Select value={tariffGroup} onValueChange={setTariffGroup} disabled={readOnly}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecione o grupo tarifário" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Grupo A">Grupo A</SelectItem>
                          <SelectItem value="Grupo B">Grupo B</SelectItem>
                          <SelectItem value="Mercado Livre">Mercado Livre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="w-full min-w-0">
                      <Label>Padrão *</Label>
                      <Select
                        value={padrao}
                        onValueChange={(v) => {
                          setPadrao(v);
                          if (v !== "Múltiplas Faturas") setFaturaMinimaKwh("");
                        }}
                        disabled={readOnly}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecione o padrão" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monofásico">Monofásico</SelectItem>
                          <SelectItem value="Bifásico">Bifásico</SelectItem>
                          <SelectItem value="Trifásico">Trifásico</SelectItem>
                          <SelectItem value="Múltiplas Faturas">Múltiplas Faturas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {padrao === "Múltiplas Faturas" && (
                      <div className="w-full min-w-0">
                        <Label>Fatura Mínima (kWh) *</Label>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          placeholder="Ex.: 130 (soma das faturas mínimas)"
                          value={faturaMinimaKwh}
                          onChange={(e) => setFaturaMinimaKwh(e.target.value)}
                          disabled={readOnly}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Soma das faturas mínimas do conjunto (ex.: trifásico 100 + monofásico 30 = 130)
                        </p>
                      </div>
                    )}
                  </div>

                  {tipoFixacao === 'Telhado' && (
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4 w-full mt-4 min-w-0">
                      <div className="w-full min-w-0">
                        <Label>Tipo de Telha *</Label>
                        <Select value={tipoTelha} onValueChange={setTipoTelha} disabled={readOnly}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecione o tipo de telha" />
                          </SelectTrigger>
                          <SelectContent>
                            {tipoTelhaOptions.map((option) => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedDimensionamento && tariffGroup && (
                <div className="w-full min-w-0 max-w-full overflow-x-hidden mt-4" style={{ maxWidth: isMobile ? '100vw' : '100%', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label>Dados Mensais</Label>
                      {!readOnly && dimensionamentosParaImportar.length > 0 && (
                        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                          <DialogTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1.5 shrink-0"
                              title="Importar dados da tabela Dados Mensais de outro dimensionamento"
                            >
                              <Copy className="h-4 w-4" />
                              Importar de outro dimensionamento
                            </Button>
                          </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Importar Dados Mensais</DialogTitle>
                            <DialogDescription>
                              Selecione um dimensionamento para copiar os dados da tabela &quot;Dados Mensais&quot; para o dimensionamento atual. Você poderá editar os valores após a importação.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-2 py-2">
                            {dimensionamentosParaImportar.map((dim) => (
                              <Button
                                key={dim.id}
                                variant="outline"
                                className="w-full justify-start gap-2"
                                onClick={() => handleImportDadosMensais(dim)}
                                disabled={isImportingDadosMensais}
                              >
                                <Copy className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{dim.nome_dimensionamento}</span>
                                {dim.descricao_dimensionamento && (
                                  <span className="text-muted-foreground text-xs truncate">
                                    — {dim.descricao_dimensionamento}
                                  </span>
                                )}
                              </Button>
                            ))}
                          </div>
                          <DialogFooter>
                            <Button variant="secondary" onClick={() => setShowImportDialog(false)}>
                              Cancelar
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                    </div>
                  </div>
                  {tariffGroup === "Grupo A" || tariffGroup === "Mercado Livre" ? (
                    <div className="relative w-full" style={{ maxWidth: isMobile ? '100vw' : '100%', width: '100%', boxSizing: 'border-box' }}>
                      <div 
                        className={`${isMobile ? 'overflow-x-auto overflow-y-hidden w-full' : 'overflow-x-auto overflow-y-hidden'} sm:mx-0 sm:px-0`} 
                        style={{ 
                          WebkitOverflowScrolling: 'touch',
                          maxWidth: isMobile ? '100vw' : '100%',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        <div className="min-w-[1600px] inline-block" style={{ boxSizing: 'border-box' }}>
                          {isMobile && (
                            <div className="mb-2 text-xs text-muted-foreground sm:hidden">
                              ← Deslize horizontalmente para ver todas as colunas →
                            </div>
                          )}
                          <Table 
                            className="dimensionamento-table"
                            style={{
                              minWidth: '1600px',
                              width: '100%',
                              tableLayout: 'fixed',
                              margin: 0,
                              boxSizing: 'border-box'
                            }}
                          >
                            <TableHeader>
                              <TableRow className="bg-blue-100">
                                <TableHead className="text-blue-800 min-w-[100px]">Mês</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Consumo Ponta (kWh)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Consumo Fora Ponta (kWh)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">TUSD Ponta (R$)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">TE Ponta (R$)</TableHead>
                                <TableHead className="text-sun7-blue font-bold min-w-[120px]">Despesa Ponta</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">TUSD Fora Ponta (R$)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">TE Fora Ponta (R$)</TableHead>
                                <TableHead className="text-sun7-blue font-bold min-w-[120px]">Despesa Fora Ponta</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Demanda Contratada (kW)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Preço Demanda (R$)</TableHead>
                                <TableHead className="text-sun7-blue font-bold min-w-[120px]">Despesa Demanda</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">COSIP (R$)</TableHead>
                                <TableHead className="text-sun7-blue font-bold min-w-[120px]">Despesa Fatura</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {months.map((month, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium">{month}</TableCell>
                                  <>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    value={monthlyData[index].consumoPonta || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].consumoPonta = e.target.value;
                                      newData[index].despesaPonta = calculateDespesaEnergia(newData[index].tePonta || "0", newData[index].tusdPonta || "0", e.target.value);
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="kWh"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('consumoPonta', monthlyData[0].consumoPonta || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    value={monthlyData[index].consumoForaPonta || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].consumoForaPonta = e.target.value;
                                      newData[index].despesaForaPonta = calculateDespesaEnergia(newData[index].teForaPonta || "0", newData[index].tusdForaPonta || "0", e.target.value);
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="kWh"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('consumoForaPonta', monthlyData[0].consumoForaPonta || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={monthlyData[index].tusdPonta || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].tusdPonta = e.target.value;
                                      newData[index].despesaPonta = calculateDespesaEnergia(newData[index].tePonta || "0", e.target.value, newData[index].consumoPonta || "0");
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="R$"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('tusdPonta', monthlyData[0].tusdPonta || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={monthlyData[index].tePonta || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].tePonta = e.target.value;
                                      newData[index].despesaPonta = calculateDespesaEnergia(e.target.value, newData[index].tusdPonta || "0", newData[index].consumoPonta || "0");
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="R$"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('tePonta', monthlyData[0].tePonta || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium text-sun7-blue font-bold">{formatBrazilianNumber(monthlyData[index].despesaPonta || "0")}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={monthlyData[index].tusdForaPonta || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].tusdForaPonta = e.target.value;
                                      newData[index].despesaForaPonta = calculateDespesaEnergia(newData[index].teForaPonta || "0", e.target.value, newData[index].consumoForaPonta || "0");
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="R$"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('tusdForaPonta', monthlyData[0].tusdForaPonta || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={monthlyData[index].teForaPonta || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].teForaPonta = e.target.value;
                                      newData[index].despesaForaPonta = calculateDespesaEnergia(e.target.value, newData[index].tusdForaPonta || "0", newData[index].consumoForaPonta || "0");
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="R$"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('teForaPonta', monthlyData[0].teForaPonta || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium text-sun7-blue font-bold">{formatBrazilianNumber(monthlyData[index].despesaForaPonta || "0")}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    value={monthlyData[index].demandaContratada || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].demandaContratada = e.target.value;
                                      newData[index].despesaDemanda = calculateDespesaDemanda(e.target.value, newData[index].precoDemanda || "0");
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="kW"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('demandaContratada', monthlyData[0].demandaContratada || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={monthlyData[index].precoDemanda || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].precoDemanda = e.target.value;
                                      newData[index].despesaDemanda = calculateDespesaDemanda(newData[index].demandaContratada || "0", e.target.value);
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="R$"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('precoDemanda', monthlyData[0].precoDemanda || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium text-sun7-blue font-bold">{formatBrazilianNumber(monthlyData[index].despesaDemanda || "0")}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={monthlyData[index].cosip || ""}
                                    onChange={(e) => {
                                      const newData = [...monthlyData];
                                      newData[index].cosip = e.target.value;
                                      setMonthlyData(newData);
                                    }}
                                    placeholder="R$"
                                    className=""
                                    disabled={readOnly}
                                  />
                                  {index === 0 && !readOnly && (
                                    <Button size="sm" variant="outline" onClick={() => replicateValue('cosip', monthlyData[0].cosip || "")} className="h-8 w-8 p-0">
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium text-sun7-blue font-bold">
                                {formatBrazilianNumber(
                                  (parseFloat(monthlyData[index].despesaPonta || "0") || 0) +
                                  (parseFloat(monthlyData[index].despesaForaPonta || "0") || 0) +
                                  (parseFloat(monthlyData[index].cosip || "0") || 0) +
                                  (parseFloat(monthlyData[index].despesaDemanda || "0") || 0)
                                )}
                              </TableCell>
                                  </>
                                </TableRow>
                              ))}
                              <TableRow className="bg-blue-50 font-bold">
                                <TableCell className="text-blue-800">Total</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalConsumoPonta)}</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalConsumoForaPonta)}</TableCell>
                                <TableCell className="text-blue-800"></TableCell>
                                <TableCell className="text-blue-800"></TableCell>
                                <TableCell className="text-sun7-blue font-bold">{formatBrazilianNumber(totals.totalDespesaPonta)}</TableCell>
                                <TableCell className="text-blue-800"></TableCell>
                                <TableCell className="text-blue-800"></TableCell>
                                <TableCell className="text-sun7-blue font-bold">{formatBrazilianNumber(totals.totalDespesaForaPonta)}</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalDemandaContratada)}</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalPrecoDemanda)}</TableCell>
                                <TableCell className="text-sun7-blue font-bold">{formatBrazilianNumber(totals.totalDespesaDemanda)}</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalCosip)}</TableCell>
                                <TableCell className="text-sun7-blue font-bold">
                                  {formatBrazilianNumber(
                                    (parseFloat(totals.totalDespesaPonta) || 0) +
                                    (parseFloat(totals.totalDespesaForaPonta) || 0) +
                                    (parseFloat(totals.totalCosip) || 0) +
                                    (parseFloat(totals.totalDespesaDemanda) || 0)
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative w-full" style={{ maxWidth: isMobile ? '100vw' : '100%', width: '100%', boxSizing: 'border-box' }}>
                      <div 
                        className={`${isMobile ? 'overflow-x-auto overflow-y-hidden w-full' : 'overflow-x-auto overflow-y-hidden'} sm:mx-0 sm:px-0`} 
                        style={{ 
                          WebkitOverflowScrolling: 'touch',
                          maxWidth: isMobile ? '100vw' : '100%',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        <div className="min-w-[800px] inline-block" style={{ boxSizing: 'border-box' }}>
                          {isMobile && (
                            <div className="mb-2 text-xs text-muted-foreground sm:hidden">
                              ← Deslize horizontalmente para ver todas as colunas →
                            </div>
                          )}
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-blue-100">
                                <TableHead className="text-blue-800 min-w-[100px]">Mês</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Consumo (kWh)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">TUSD (R$)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">TE (R$)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Despesa Energia</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">COSIP (R$)</TableHead>
                                <TableHead className="text-blue-800 min-w-[120px]">Despesa Fatura</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {months.map((month, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium">{month}</TableCell>
                                  <TableCell className="min-w-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Input
                                        type="number"
                                        value={monthlyData[index].consumo || ""}
                                        onChange={(e) => {
                                          const newData = [...monthlyData];
                                          newData[index].consumo = e.target.value;
                                          newData[index].despesaEnergia = calculateDespesaEnergia(newData[index].te || "0", newData[index].tusd || "0", e.target.value);
                                          newData[index].despesaFatura = calculateDespesaFatura(newData[index].despesaEnergia, newData[index].cosip || "0");
                                          setMonthlyData(newData);
                                        }}
                                        placeholder="kWh"
                                        className="min-w-0 flex-1"
                                        disabled={readOnly}
                                      />
                                      {index === 0 && !readOnly && (
                                        <Button size="sm" variant="outline" onClick={() => replicateValue('consumo', monthlyData[0].consumo || "")} className="h-8 w-8 p-0">
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={monthlyData[index].tusd || ""}
                                        onChange={(e) => {
                                          const newData = [...monthlyData];
                                          newData[index].tusd = e.target.value;
                                          newData[index].despesaEnergia = calculateDespesaEnergia(newData[index].te || "0", e.target.value, newData[index].consumo || "0");
                                          newData[index].despesaFatura = calculateDespesaFatura(newData[index].despesaEnergia, newData[index].cosip || "0");
                                          setMonthlyData(newData);
                                        }}
                                        placeholder="R$"
                                        className=""
                                        disabled={readOnly}
                                      />
                                      {index === 0 && !readOnly && (
                                        <Button size="sm" variant="outline" onClick={() => replicateValue('tusd', monthlyData[0].tusd || "")} className="h-8 w-8 p-0">
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={monthlyData[index].te || ""}
                                        onChange={(e) => {
                                          const newData = [...monthlyData];
                                          newData[index].te = e.target.value;
                                          newData[index].despesaEnergia = calculateDespesaEnergia(e.target.value, newData[index].tusd || "0", newData[index].consumo || "0");
                                          newData[index].despesaFatura = calculateDespesaFatura(newData[index].despesaEnergia, newData[index].cosip || "0");
                                          setMonthlyData(newData);
                                        }}
                                        placeholder="R$"
                                        className=""
                                        disabled={readOnly}
                                      />
                                      {index === 0 && !readOnly && (
                                        <Button size="sm" variant="outline" onClick={() => replicateValue('te', monthlyData[0].te || "")} className="h-8 w-8 p-0">
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-left font-medium text-sun7-blue font-bold">{formatBrazilianNumber(monthlyData[index].despesaEnergia || "0")}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={monthlyData[index].cosip || ""}
                                        onChange={(e) => {
                                          const newData = [...monthlyData];
                                          newData[index].cosip = e.target.value;
                                          newData[index].despesaFatura = calculateDespesaFatura(newData[index].despesaEnergia || "0", e.target.value);
                                          setMonthlyData(newData);
                                        }}
                                        placeholder="R$"
                                        className=""
                                      />
                                      {index === 0 && (
                                        <Button size="sm" variant="outline" onClick={() => replicateValue('cosip', monthlyData[0].cosip || "")} className="h-8 w-8 p-0">
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-left font-medium text-sun7-blue font-bold">{formatBrazilianNumber(monthlyData[index].despesaFatura || "0")}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="bg-blue-50 font-bold">
                                <TableCell className="text-blue-800">Total</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalConsumo)}</TableCell>
                                <TableCell className="text-blue-800"></TableCell>
                                <TableCell className="text-blue-800"></TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalDespesaEnergia)}</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalCosip)}</TableCell>
                                <TableCell className="text-blue-800">{formatBrazilianNumber(totals.totalDespesaFatura)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className={`flex ${isMobile ? 'flex-col gap-4' : 'justify-between'} w-full min-w-0 max-w-full mt-4`}>
                {!readOnly && (
                  <Button
                    onClick={handleSaveData}
                    disabled={
                      !hasFilledData() || 
                      (isDataSaved && !hasUnsavedChanges)
                    }
                    className={isMobile ? 'w-full' : ''}
                    title={
                      !hasFilledData() 
                        ? "Preencha todos os campos obrigatórios antes de salvar"
                        : (isDataSaved && !hasUnsavedChanges)
                          ? "Nenhuma alteração foi feita nos dados salvos"
                          : !isDataSaved
                            ? "Clique para salvar os dados"
                            : "Clique para salvar as alterações"
                    }
                  >
                    Salvar Dados
                  </Button>
                )}
                <Button
                  onClick={() => {
                    onNext(selectedDimensionamento?.id);
                  }}
                  disabled={!canProceedToNext() || !selectedDimensionamento || readOnly}
                  className={isMobile ? 'w-full' : ''}
                  title={
                    readOnly
                      ? "Perfil parceiro: apenas visualização permitida"
                      : !selectedDimensionamento
                        ? "Selecione um dimensionamento antes de prosseguir"
                        : !hasFilledData() 
                          ? "Preencha todos os campos obrigatórios antes de prosseguir"
                          : !isDataSaved 
                            ? "Salve os dados antes de prosseguir"
                            : hasUnsavedChanges 
                              ? "Existem alterações não salvas. Salve os dados antes de prosseguir"
                              : "Clique para prosseguir para a próxima etapa"
                  }
                >
                  Próximo
                </Button>
              </div>

              {isDataSaved && !hasUnsavedChanges && (
                <Alert className="border-green-200 bg-green-50 w-full min-w-0 max-w-full">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <AlertTitle className="text-green-800 break-words">Dados Confirmados</AlertTitle>
                  <AlertDescription className="text-green-700 break-words">
                    Os dados do cliente foram salvos com sucesso. Clique em "Próximo" para continuar.
                  </AlertDescription>
                </Alert>
              )}

              {!isDataSaved && hasFilledData() && (
                <Alert className="border-yellow-200 bg-yellow-50 w-full min-w-0 max-w-full">
                  <AlertTitle className="text-yellow-800 break-words">Dados Não Salvos</AlertTitle>
                  <AlertDescription className="text-yellow-700 break-words">
                    Os dados foram preenchidos mas ainda não foram salvos. Clique em "Salvar Dados" antes de prosseguir.
                  </AlertDescription>
                </Alert>
              )}

              {hasUnsavedChanges && (
                <Alert className="border-orange-200 bg-orange-50 w-full min-w-0 max-w-full">
                  <AlertTitle className="text-orange-800 break-words">Alterações Não Salvas</AlertTitle>
                  <AlertDescription className="text-orange-700 break-words">
                    Existem alterações não salvas. Salve os dados antes de continuar para a próxima etapa.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
